import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Phone, Loader2, Eye, EyeOff, Building2, CheckCircle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function RegisterPartner() {
  const [form, setForm] = useState({
    email: '', password: '', confirmPassword: '',
    company_name: '', phone: '', vat_number: '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [success, setSuccess] = useState(false);
  const [privacyRead, setPrivacyRead] = useState(false);
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const { login: authLogin } = useAuth();
  const navigate = useNavigate();

  const updateField = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (form.password !== form.confirmPassword) {
      setError('Le password non corrispondono');
      return;
    }
    if (form.password.length < 8) {
      setError('La password deve avere almeno 8 caratteri');
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(`${API}/api/auth/register-partner`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: form.email,
          password: form.password,
          company_name: form.company_name,
          phone: form.phone,
          vat_number: form.vat_number,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Errore nella registrazione');

      // Auto-login with returned token
      localStorage.setItem('access_token', data.access_token);
      setSuccess(true);

      // Redirect after brief success message
      setTimeout(() => {
        window.location.href = '/';
      }, 2000);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center p-4"
      style={{ background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)' }}
      data-testid="register-partner-page"
    >
      <div className="w-full max-w-lg">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-sky-500 rounded-2xl mb-4">
            <Phone className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-slate-800" style={{ fontFamily: 'Manrope' }}>
            VoiceBook
          </h1>
          <p className="text-slate-500 mt-1">Registra la tua azienda come Partner</p>
        </div>

        {success ? (
          <div className="bg-white rounded-xl border border-green-200 p-8 shadow-sm text-center">
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-slate-800 mb-2" style={{ fontFamily: 'Manrope' }}>
              Registrazione completata!
            </h2>
            <p className="text-slate-600 mb-2">Il tuo account Partner è attivo con <strong>14 giorni di trial gratuito</strong>.</p>
            <p className="text-slate-500 text-sm">Reindirizzamento alla dashboard...</p>
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-slate-200 p-8 shadow-sm">
            <div className="flex items-center gap-3 mb-6">
              <Building2 className="w-6 h-6 text-sky-500" />
              <h2 className="text-xl font-semibold text-slate-800" style={{ fontFamily: 'Manrope' }}>
                Registrazione Partner
              </h2>
            </div>

            <div className="bg-sky-50 border border-sky-200 rounded-lg p-3 mb-6">
              <p className="text-sky-700 text-sm font-medium">14 giorni di trial gratuito inclusi</p>
              <p className="text-sky-600 text-xs mt-1">Un unico account per gestire una o piu cliniche. Dopo la registrazione potrai creare le tue sedi, configurare reparti, medici e attivare l'assistente AI per ciascuna.</p>
            </div>

            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700" data-testid="register-error">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="company_name">Nome Azienda *</Label>
                <Input id="company_name" value={form.company_name} onChange={e => updateField('company_name', e.target.value)}
                  placeholder="Es. Studio Medico Rossi" required data-testid="company-name-input" />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="phone">Telefono *</Label>
                  <Input id="phone" type="tel" value={form.phone} onChange={e => updateField('phone', e.target.value)}
                    placeholder="+39 333 1234567" required data-testid="phone-input" />
                </div>
                <div>
                  <Label htmlFor="vat_number">Partita IVA *</Label>
                  <Input id="vat_number" value={form.vat_number} onChange={e => updateField('vat_number', e.target.value)}
                    placeholder="IT12345678901" required data-testid="vat-input" />
                </div>
              </div>

              <div>
                <Label htmlFor="email">Email *</Label>
                <Input id="email" type="email" value={form.email} onChange={e => updateField('email', e.target.value)}
                  placeholder="info@studiomedico.it" required data-testid="email-input" />
              </div>

              <div>
                <Label htmlFor="password">Password *</Label>
                <div className="relative">
                  <Input id="password" type={showPassword ? 'text' : 'password'} value={form.password}
                    onChange={e => updateField('password', e.target.value)}
                    placeholder="Almeno 8 caratteri" required data-testid="password-input" />
                  <button type="button" onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div>
                <Label htmlFor="confirmPassword">Conferma Password *</Label>
                <Input id="confirmPassword" type="password" value={form.confirmPassword}
                  onChange={e => updateField('confirmPassword', e.target.value)}
                  placeholder="Ripeti la password" required data-testid="confirm-password-input" />
              </div>

              <div className="flex items-start gap-2.5">
                <input type="checkbox" id="privacy_consent" required
                  checked={privacyAccepted}
                  disabled={!privacyRead}
                  onChange={e => setPrivacyAccepted(e.target.checked)}
                  className="mt-1 w-4 h-4 rounded border-slate-300 text-sky-500 focus:ring-sky-500 disabled:opacity-40 disabled:cursor-not-allowed"
                  data-testid="privacy-consent-checkbox" />
                <label htmlFor="privacy_consent" className="text-xs text-slate-500 leading-relaxed">
                  Ho letto e accetto l'<a href="/privacy" target="_blank" rel="noopener noreferrer"
                    onClick={() => setPrivacyRead(true)}
                    className="text-sky-500 hover:underline font-medium">Informativa sulla Privacy</a> e
                  acconsento al trattamento dei miei dati personali ai sensi del Regolamento UE 2016/679 (GDPR). *
                  {!privacyRead && (
                    <span className="block text-amber-500 text-[10px] mt-1">
                      Leggi l'informativa sulla privacy per abilitare il consenso
                    </span>
                  )}
                </label>
              </div>

              <Button type="submit" disabled={loading} className="w-full bg-sky-500 hover:bg-sky-600 text-white" data-testid="register-submit-btn">
                {loading ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Registrazione in corso...</> : 'Registrati come Partner'}
              </Button>
            </form>

            <div className="mt-6 text-center">
              <p className="text-sm text-slate-500">
                Hai già un account?{' '}
                <Link to="/login" className="text-sky-500 hover:text-sky-600 font-medium" data-testid="login-link">
                  Accedi qui
                </Link>
              </p>
            </div>

            <div className="mt-5 pt-4 border-t border-slate-100 text-center">
              <a href="https://telcavoip.ai" target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-[11px] text-slate-400 hover:text-sky-500 transition-colors"
                data-testid="telcavoip-badge">
                Made with <span className="text-red-400">&hearts;</span> by <span className="font-semibold text-slate-500 hover:text-sky-500">TelcaVoIP.AI</span>
              </a>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
